import { Collection, Model, tools } from '@type-r/models';
import { asDate, FormLabel, FormValue, InlineTable, PrevFormValue, TableCell, TableHeader, TableRow } from 'lqx-react';
import * as React from 'react';
import { Fragment, useMemo } from 'react';
const { notEqual } = tools;

export const DiffsTable = ({ children }) =>
    <InlineTable
        head={
            <TableHeader>
                <TableCell>
                    Changed Field
                </TableCell>
                <TableCell>
                    Old Value
                </TableCell>
                <TableCell>
                    New Value
                </TableCell>
            </TableHeader>
        }
    >
        { children }
    </InlineTable>

function safeValueToText(value): string {
    return (
        value instanceof Date ? asDate(value) :
        value ? value.toString() :
        "null"
    );
}

export const ModelDiff = ({ label, fetched, current }) =>{
    const diff = useMemo( () =>
        compareModels( fetched, current ),
        [(current as any)._changeToken]
    );
    return diff.length ?
        <>
            <TableRow className="group dark">
                <TableCell colSpan={4}>
                    { label }
                </TableCell>
            </TableRow>
            { diff.map( diff =>
                <TableRow key={ label + diff.key }>
                    <TableCell>
                        <FormLabel>{ diff.key }</FormLabel>
                    </TableCell>
                    <TableCell>
                        <PrevFormValue>{ safeValueToText(diff.prev) }</PrevFormValue>
                    </TableCell>
                    <TableCell>
                        <FormValue>{ safeValueToText(diff.next) }</FormValue>
                    </TableCell>
                </TableRow>
            )}
        </>
    : <></>
}

export const CollectionDiff = ({ fetched, current, titleAttr }) =>{
    const diff = useMemo( () =>
        compareCollections( fetched, current, titleAttr ),
        [(current as any)._changeToken]
    );
    return diff.length ?
        <>
            { diff.map( row =>
                <Fragment key={row.name}>
                    <TableRow className="group dark">
                        <TableCell colSpan={4}>
                            Part # { row.name }
                        </TableCell>
                    </TableRow>
                    { row.diff.map( diff =>
                        <TableRow key={ row.name + diff.key }>
                            <TableCell>
                                <FormLabel>{ diff.key }</FormLabel>
                            </TableCell>
                            <TableCell>
                                <PrevFormValue>{ diff.prev }</PrevFormValue>
                            </TableCell>
                            <TableCell>
                                <FormValue>{ diff.next }</FormValue>
                            </TableCell>
                        </TableRow>
                    )}
                </Fragment>
            )}
        </>
    : <></>
}

function compareCollections( prev : Collection, next : Collection, title = 'id' ) : {
    name : string,
    diff : Diff[]
}[]{
    const rows = [];

    const merged = new ( Collection.ofRefs( Model ) );
    merged.add( prev.models );
    merged.add( next.models );

    merged.forEach( model => {
        if( !next.get( model ) ){
            rows.push({
                name : model[ title ],
                diff : addRemoveModel( model, 'prev' )
            })
        }
        else if( !prev.get( model ) ){
            rows.push({
                name : model[ title ],
                diff : addRemoveModel( model, 'next' )
            })
        }
        else{
            const diff = compareModels(
                prev.get( model ),
                next.get( model )
            );

            diff.length && rows.push({
                name : model[ title ],
                diff
            })
        }
    });

    return rows;
}

type Diff = { key : string, prev? : any, next? : any }
function addRemoveModel( a_model : Model, col : 'prev' | 'next' ) : Diff[] {
    const a = flatten( a_model ),
        diff = [];

    for( let i = 0; i < a.length; i++ ){
        diff.push({
            key : a[ i ].key,
            [ col ] : a[ i ].value
        });
    }

    return diff;
}

export function compareModels( a_model : Model, b_model : Model ) : Diff[]{
    const a = flatten( a_model ),
        b = flatten( b_model ),
        diff = [];

    for( let i = 0; i < a.length; i++ ){
        if( a[ i ].value !== b[ i ].value &&
            !(a[ i ].value instanceof Date &&
             b[ i ].value instanceof Date &&
             Math.abs(a[ i ].value.getTime() - b[ i ].value.getTime()) < 100
            )){
            diff.push({ key : a[ i ].key, prev : a[ i ].value, next : b[ i ].value });
        }
    }

    return diff;
}

/**
 * Flatten the nested model/collection tree.
 */
function flatten( m : Model, attrs = [] ) : { key : string, value : any }[] {
    m.forEach( ( value, key ) => {
        if( value instanceof Model ){
            flatten( value, attrs );
        }
        else if( !(value instanceof Collection) ){
            key === 'id' || attrs.push({ key, value });
        }
    });

    return attrs;
}

export function getModelsJsonDiff<M extends Model>( prev : M, next : M, serializeOptions? ) : object {
    return getJSONDiff( prev.toJSON(serializeOptions), next.toJSON(serializeOptions) );
}

export function getJSONDiff( a_prev : object, a_next : object ){
    const diff = {};
    // tslint:disable-next-line: forin
    for( let key in a_next ){
        const next = a_next[ key ],
            prev = a_prev[ key ];
        if( next !== prev ){
            if( next == null ){
                diff[ key ] = null;
            }
            else if( prev == null ){
                diff[ key ] = next;
            }
            else if( typeof next === 'object' ) {
                if( notEqual( next, prev ) ){
                    if( Array.isArray( next ) ){
                        diff[key] = [];
                        prev.forEach(item => {
                            const possiblyChanged = next.find(x => x.id === item.id);
                            if (possiblyChanged) {
                                if (notEqual(item, possiblyChanged)) {
                                    // changed item
                                    const changes = getJSONDiff(item, possiblyChanged);
                                    diff[key].push({ id: item.id, ...changes });
                                }
                            } else {
                                // removed item
                                diff[key].push({ id: item.id, _disposed : true });
                            }
                        });
                        next.forEach(item => {
                            if (!prev.some(x => x.id === item.id)) {
                                // new item
                                diff[key].push(item);
                            }
                        });
                    }
                    else{
                        diff[ key ] = getJSONDiff( prev, next );
                    }
                }
            }
            else {
                diff[ key ] = next;
            }
        }
    }
    return diff;
}

export function getChangedFieldsNames( prev : any, next : any ) : string[] {
    const changedFields = getJSONDiff( prev, next );

    const result = {};
    function flatProps(model) {
        // tslint:disable-next-line: forin
        for (const el in model) {
            if (typeof model[el] === 'object') {
                if ( Array.isArray( model[el] ) ) {
                    for (const item of model[el]) {
                        flatProps(item);
                    }
                } else {
                    flatProps(model[el]);
                }

                continue;
            }

            result[el] = null;
        }
    }

    flatProps(changedFields);
    return Object.keys(result)
        .filter(x => x !== 'id');
}

export function getChangesCount(changes: any): number;
export function getChangesCount(prev: any, next?: any): number {
    const changedFields = next ?
        getJSONDiff( prev, next ) :
        prev;

    let result = 0;
    function flatProps(model) {
        // tslint:disable-next-line: forin
        for (const el in model) {
            if (typeof model[el] === 'object') {
                if ( Array.isArray( model[el] ) ) {
                    for (const item of model[el]) {
                        flatProps(item);
                    }
                } else {
                    flatProps(model[el]);
                }

                continue;
            }

            if (el !== 'id') {
                ++result;
            }
        }
    }

    flatProps(changedFields);
    return result;
}

export interface DeltaDiff {
    oldValues: any;
    newValues: any;
}

export function getModelsJsonDeltaDiff<M extends Model>( prev: M, next: M, serializeOptions? ): DeltaDiff {
    return getJSONDeltaDiff( prev.toJSON(serializeOptions), next.toJSON(serializeOptions) );
}

export function getJSONDeltaDiff( a_prev: object, a_next: object, oldValues = {}, newValues = {} ): DeltaDiff {
    // tslint:disable-next-line: forin
    for ( let key in a_next ) {
        const next = a_next[ key ],
            prev = a_prev[ key ];
        if ( next !== prev ) {
            if ( next == null ) {
                oldValues[ key ] = prev;
                newValues[ key ] = null;
            } else if ( prev == null ) {
                oldValues[ key ] = null;
                newValues[ key ] = next;
            } else if ( typeof next === 'object' ) {
                if ( notEqual( next, prev ) ) {
                    if ( Array.isArray( next ) ) {
                        oldValues[ key ] = [];
                        newValues[ key ] = [];
                        prev.forEach(item => {
                            const possiblyChanged = next.find(x => x.id === item.id);
                            if (possiblyChanged) {
                                if (notEqual(item, possiblyChanged)) {
                                    // changed item
                                    const changes = getJSONDeltaDiff(item, possiblyChanged);
                                    oldValues[key].push({ id: item.id, ...changes.oldValues });
                                    newValues[key].push({ id: item.id, ...changes.newValues });
                                }
                            } else {
                                // removed item
                                oldValues[key].push(item);
                                newValues[key].push({ id: item.id, _disposed : true });
                            }
                        });
                        next.forEach(item => {
                            if (!prev.some(x => x.id === item.id)) {
                                // new item
                                newValues[key].push(item);
                            }
                        });
                    } else {
                        oldValues[ key ] = {};
                        newValues[ key ] = {};
                        getJSONDeltaDiff( prev, next, oldValues[ key ], newValues[ key ] );
                    }
                }
            } else {
                oldValues[ key ] = prev;
                newValues[ key ] = next;
            }
        }
    }

    return {
        oldValues,
        newValues
    };
}
